import { createContext, createMemo, createSignal, onMount, useContext } from "solid-js"
import type { Accessor, ParentComponent } from "solid-js"
import { storage, ConfigOwnerReconciliationPendingError, type OwnerBucket } from "../lib/storage"
import type { RemoteServerProfile, UpdateFeed } from "../../../server/src/api-types"
import {
  ensureInstanceConfigLoaded,
  getInstanceConfig,
  updateInstanceConfig as updateInstanceData,
} from "./instance-config"
import { getLogger } from "../lib/logger"
import { loadSpeechCapabilities, resetSpeechCapabilities } from "./speech"
import { buildSpeechPatch } from "../lib/speech-patch"
import { normalizeHotkeyOverrides, type HotkeyBinding, type HotkeyOverrides } from "../lib/hotkey-bindings"
import { normalizeAppearancePreferences, selectAppearancePalette, type Appearance } from "../lib/appearance-preferences"
import { defaultNotificationRouting, normalizeNotificationRouting, type NotificationRouting } from "../lib/notification-routing"
import { encodeMissionDefaults, normalizeMissionDefaults, validMissionDefaults, type MissionProfileDefault } from "../lib/mission-defaults"
import { normalizeMissionModels, validMissionModels, parseMissionModel, saveMissionModelRecord, removeMissionModelRecord, type UserMissionModel } from "../lib/mission-model-library"
import { missionPreferenceExpectation, missionPreferenceValue, type MissionPreferenceKey, type MissionPreferenceExpectation } from "../lib/mission-preferences-document"
import {
  isColorSchemeColors,
  normalizeColorScheme,
  toColorSchemeMergePatch,
  type ColorSchemeColors,
  type ColorSchemeId,
  type NormalizedColorScheme,
} from "../lib/theme-scheme"
import {
  createColorSchemePresetId,
  MAX_COLOR_SCHEME_PRESETS,
  normalizeColorSchemeOverrides,
  isDefaultCustomColors,
  normalizeColorSchemePresets,
  type UserColorSchemePresets,
} from "../lib/color-scheme-presets"
import {
  normalizeModelVisibilityPreference,
  normalizeModelVisibilityPreferences,
  type ModelVisibilityPreference,
  type ModelVisibilityPreferences,
} from "../lib/model-visibility"

const log = getLogger("actions")

type DeepReadonly<T> = T extends (...args: any[]) => unknown
  ? T
  : T extends Array<infer U>
    ? ReadonlyArray<DeepReadonly<U>>
    : T extends object
      ? { readonly [K in keyof T]: DeepReadonly<T[K]> }
      : T

export interface ModelPreference {
  providerId: string
  modelId: string
}

export type DiffViewMode = "split" | "unified"
export type FollowUpBehavior = "steer" | "queue"
export type ExpansionPreference = "expanded" | "collapsed"
export type VisibilityPreference = "hidden" | ExpansionPreference
export type ToolCallExpansionPreset = "nothing" | "minimal" | "balanced" | "everything"
export type ToolCallExpansionPresetSelection = ToolCallExpansionPreset | "custom"
export type ToolInputsVisibilityPreference = VisibilityPreference
export type ListeningMode = "local" | "all"
export type ServerLogLevel = "DEBUG" | "INFO" | "WARN" | "ERROR"
export type SpeechProviderPreference = "openai-compatible"
export type SpeechPlaybackMode = "streaming" | "buffered"
export type SpeechTtsFormat = "mp3" | "wav" | "opus" | "aac"

export interface ToolCallExpansionDefaults {
  preset: ToolCallExpansionPresetSelection
  thinking: ExpansionPreference
  tools: Record<string, VisibilityPreference>
}

export interface SpeechSettings {
  provider: SpeechProviderPreference
  apiKey?: string
  hasApiKey: boolean
  baseUrl?: string
  sttModel: string
  ttsModel: string
  ttsVoice: string
  playbackMode: SpeechPlaybackMode
  ttsFormat: SpeechTtsFormat
  separateProviders: boolean
  stt: {
    apiKey?: string
    hasApiKey: boolean
    baseUrl?: string
    model: string
  }
  tts: {
    apiKey?: string
    hasApiKey: boolean
    baseUrl?: string
    model: string
  }
}

export type SpeechSettingsUpdate = Partial<Omit<SpeechSettings, "provider" | "hasApiKey" | "apiKey" | "baseUrl" | "sttModel" | "ttsModel" | "ttsVoice" | "stt" | "tts">> & {
  apiKey?: string | null
  baseUrl?: string | null
  sttModel?: string | null
  ttsModel?: string | null
  ttsVoice?: string | null
  separateProviders?: boolean
  stt?: { apiKey?: string | null; baseUrl?: string | null; model?: string | null }
  tts?: { apiKey?: string | null; baseUrl?: string | null; model?: string | null }
}

export interface UiSettings {
  showThinkingBlocks: boolean
  thinkingBlocksExpansion: ExpansionPreference
  showMessageTimeline: boolean
  holdLongAssistantReplies: boolean
  promptSubmitOnEnter: boolean
  hotkeyOverrides: HotkeyOverrides
  followUpBehavior: FollowUpBehavior
  showPromptVoiceInput: boolean
  locale?: string
  diffViewMode: DiffViewMode
  /** Files panel diff readers compare lines without leading/trailing whitespace. */
  hideDiffWhitespaceChanges: boolean
  toolCallExpansionDefaults: ToolCallExpansionDefaults
  toolOutputExpansion: ExpansionPreference
  diagnosticsExpansion: VisibilityPreference
  systemMessagesVisibility: VisibilityPreference
  toolInputsVisibility: ToolInputsVisibilityPreference
  showUsageMetrics: boolean
  usageMetricsExpansion: ExpansionPreference
  showProviderUsageCreditBalance: boolean
  autoCleanupBlankSessions: boolean
  keepUnseenSubagentIdleStatus: boolean
  focusExistingWindowOnSecondLaunch: boolean
  modelVisibility: ModelVisibilityPreferences
  missionProfileDefaults: MissionProfileDefault[]
  missionModels: UserMissionModel[]

  /** Per notification category: on screen, in the history (badge) and/or as a system notification. */
  notificationRouting: NotificationRouting

  /** System notifications are also sent while this window is visible. */
  osNotificationsAllowWhenVisible: boolean
  /** Legacy system switch and per-event toggles; only seed `notificationRouting` until it is first saved. */
  osNotificationsEnabled: boolean
  notifyOnNeedsInput: boolean
  notifyOnIdle: boolean
}

// Backwards-compatible alias for older imports.
export type Preferences = UiSettings

export interface OpenCodeBinary {
  path: string
  version?: string
  lastUsed: number
  label?: string
}

export interface RecentFolder {
  path: string
  lastAccessed: number
  projectName?: string
}

export type ThemePreference = "light" | "dark" | "system"

interface UiConfigBucket {
  theme?: ThemePreference
  colorScheme?: unknown
  customColorScheme?: unknown
  settings?: Partial<UiSettings>
}

interface ServerConfigBucket {
  listeningMode?: ListeningMode
  logLevel?: ServerLogLevel
  environmentVariables?: Record<string, string>
  secureEnvVars?: string[]
  opencodeBinary?: string
  speech?: Partial<SpeechSettings>
}

interface UiStateBucket {
  appearancePreferences?: unknown
  theme?: ThemePreference
  colorScheme?: unknown
  customColorScheme?: unknown
  colorSchemeOverrides?: unknown
  colorSchemePresets?: unknown
  activeColorSchemePresetId?: string
  recentFolders?: RecentFolder[]
  opencodeBinaries?: OpenCodeBinary[]
  remoteServers?: RemoteServerProfile[]
  models?: {
    recents?: ModelPreference[]
    favorites?: ModelPreference[]
    thinkingSelections?: Record<string, string>
    favoritesOnly?: boolean
  }
}

interface NormalizedUiState {
  recentFolders: RecentFolder[]
  opencodeBinaries: OpenCodeBinary[]
  remoteServers: RemoteServerProfile[]
  models: {
    recents: ModelPreference[]
    favorites: ModelPreference[]
    thinkingSelections: Record<string, string>
    favoritesOnly: boolean
  }
}

const MAX_RECENT_FOLDERS = 20
const MAX_RECENT_MODELS = 5
const MAX_FAVORITE_MODELS = 50

const defaultToolCallExpansionDefaults: ToolCallExpansionDefaults = {
  preset: "balanced",
  thinking: "collapsed",
  tools: {},
}

const defaultUiSettings: UiSettings = {
  showThinkingBlocks: false,
  thinkingBlocksExpansion: "collapsed",
  showMessageTimeline: true,
  holdLongAssistantReplies: true,
  promptSubmitOnEnter: true,
  hotkeyOverrides: {},
  followUpBehavior: "steer",
  showPromptVoiceInput: true,
  diffViewMode: "split",
  hideDiffWhitespaceChanges: true,
  toolCallExpansionDefaults: defaultToolCallExpansionDefaults,
  toolOutputExpansion: "expanded",
  diagnosticsExpansion: "expanded",
  systemMessagesVisibility: "hidden",
  toolInputsVisibility: "collapsed",
  showUsageMetrics: false,
  usageMetricsExpansion: "collapsed",
  showProviderUsageCreditBalance: false,
  autoCleanupBlankSessions: true,
  keepUnseenSubagentIdleStatus: true,
  focusExistingWindowOnSecondLaunch: false,
  modelVisibility: {},
  missionProfileDefaults: [],
  missionModels: [],

  notificationRouting: defaultNotificationRouting,

  osNotificationsEnabled: false,
  osNotificationsAllowWhenVisible: false,
  notifyOnNeedsInput: true,
  notifyOnIdle: true,
}

function normalizeExpansionPreference(value: unknown, fallback: ExpansionPreference): ExpansionPreference {
  return value === "expanded" || value === "collapsed" ? value : fallback
}

function normalizeVisibilityPreference(value: unknown, fallback: VisibilityPreference): VisibilityPreference {
  return value === "hidden" || value === "expanded" || value === "collapsed" ? value : fallback
}

function normalizeToolCallExpansionPreset(value: unknown): ToolCallExpansionPresetSelection {
  if (value === "nothing" || value === "minimal" || value === "balanced" || value === "everything" || value === "custom") {
    return value
  }
  return defaultToolCallExpansionDefaults.preset
}

// The retired "Detailed" preset becomes Custom with its effective choices: it
// expanded every tool except these, including tools added after it was saved.
const retiredDetailedTools: Record<string, VisibilityPreference> = { other: "expanded", read: "collapsed", skill: "collapsed", invalid: "collapsed" }

function normalizeToolCallExpansionTools(value: unknown): Record<string, VisibilityPreference> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {}
  const next: Record<string, VisibilityPreference> = {}
  for (const [tool, mode] of Object.entries(value as Record<string, unknown>)) {
    if (!tool) continue
    if (mode === "hidden" || mode === "expanded" || mode === "collapsed") {
      next[tool] = mode
    }
  }
  return next
}

export function normalizeToolCallExpansionDefaults(input: unknown, legacySettings: Partial<UiSettings>): ToolCallExpansionDefaults {
  const source = input && typeof input === "object" && !Array.isArray(input)
    ? (input as { [K in keyof ToolCallExpansionDefaults]?: unknown })
    : undefined
  const legacyThinking = normalizeExpansionPreference(
    legacySettings.thinkingBlocksExpansion,
    "collapsed",
  )

  const tools = normalizeToolCallExpansionTools(source?.tools)
  if (source?.preset === "detailed") {
    return { preset: "custom", thinking: normalizeExpansionPreference(source.thinking, legacyThinking), tools: { ...retiredDetailedTools, ...tools } }
  }
  return {
    preset: normalizeToolCallExpansionPreset(source?.preset),
    thinking: normalizeExpansionPreference(source?.thinking, legacyThinking),
    tools,
  }
}

const defaultSpeechSettings: SpeechSettings = {
  provider: "openai-compatible",
  hasApiKey: false,
  sttModel: "gpt-4o-mini-transcribe",
  ttsModel: "gpt-4o-mini-tts",
  ttsVoice: "alloy",
  playbackMode: "streaming",
  ttsFormat: "mp3",
  separateProviders: false,
  stt: {
    hasApiKey: false,
    model: "gpt-4o-mini-transcribe",
  },
  tts: {
    hasApiKey: false,
    model: "gpt-4o-mini-tts",
  },
}

function normalizeUiSettings(input?: Partial<UiSettings> | null): UiSettings {
  const sanitized = input ?? {}
  const toolCallExpansionDefaults = normalizeToolCallExpansionDefaults(sanitized.toolCallExpansionDefaults, sanitized)
  const usageMetricsFallback =
    toolCallExpansionDefaults.preset === "nothing" || toolCallExpansionDefaults.preset === "minimal" || toolCallExpansionDefaults.preset === "balanced"
      ? "collapsed"
      : "expanded"
  return {
    showThinkingBlocks: sanitized.showThinkingBlocks ?? defaultUiSettings.showThinkingBlocks,
    thinkingBlocksExpansion: sanitized.thinkingBlocksExpansion ?? defaultUiSettings.thinkingBlocksExpansion,
    showMessageTimeline: sanitized.showMessageTimeline ?? defaultUiSettings.showMessageTimeline,
    holdLongAssistantReplies: sanitized.holdLongAssistantReplies ?? defaultUiSettings.holdLongAssistantReplies,
    promptSubmitOnEnter: sanitized.promptSubmitOnEnter ?? defaultUiSettings.promptSubmitOnEnter,
    hotkeyOverrides: normalizeHotkeyOverrides(sanitized.hotkeyOverrides),
    followUpBehavior:
      sanitized.followUpBehavior === "queue" || sanitized.followUpBehavior === "steer"
        ? sanitized.followUpBehavior
        : defaultUiSettings.followUpBehavior,
    showPromptVoiceInput: sanitized.showPromptVoiceInput ?? defaultUiSettings.showPromptVoiceInput,
    locale: sanitized.locale ?? defaultUiSettings.locale,
    diffViewMode: sanitized.diffViewMode ?? defaultUiSettings.diffViewMode,
    hideDiffWhitespaceChanges: sanitized.hideDiffWhitespaceChanges ?? defaultUiSettings.hideDiffWhitespaceChanges,
    toolCallExpansionDefaults,
    toolOutputExpansion: sanitized.toolOutputExpansion ?? defaultUiSettings.toolOutputExpansion,
    diagnosticsExpansion: normalizeVisibilityPreference(
      sanitized.diagnosticsExpansion,
      defaultUiSettings.diagnosticsExpansion,
    ),
    systemMessagesVisibility: normalizeVisibilityPreference(sanitized.systemMessagesVisibility, "hidden"),
    toolInputsVisibility:
      sanitized.toolInputsVisibility === "hidden" || sanitized.toolInputsVisibility === "collapsed" || sanitized.toolInputsVisibility === "expanded"
        ? sanitized.toolInputsVisibility
        : defaultUiSettings.toolInputsVisibility,
    showUsageMetrics: sanitized.showUsageMetrics ?? defaultUiSettings.showUsageMetrics,
    usageMetricsExpansion: normalizeExpansionPreference(
      sanitized.usageMetricsExpansion,
      usageMetricsFallback,
    ),
    showProviderUsageCreditBalance:
      sanitized.showProviderUsageCreditBalance ?? defaultUiSettings.showProviderUsageCreditBalance,
    autoCleanupBlankSessions: sanitized.autoCleanupBlankSessions ?? defaultUiSettings.autoCleanupBlankSessions,
    keepUnseenSubagentIdleStatus:
      sanitized.keepUnseenSubagentIdleStatus ?? defaultUiSettings.keepUnseenSubagentIdleStatus,
    focusExistingWindowOnSecondLaunch: sanitized.focusExistingWindowOnSecondLaunch === true,
    modelVisibility: normalizeModelVisibilityPreferences(sanitized.modelVisibility),
    missionProfileDefaults: normalizeMissionDefaults(sanitized.missionProfileDefaults),
    missionModels: normalizeMissionModels(sanitized.missionModels),
    notificationRouting: normalizeNotificationRouting(sanitized.notificationRouting, sanitized),
    osNotificationsEnabled: sanitized.osNotificationsEnabled ?? defaultUiSettings.osNotificationsEnabled,
    osNotificationsAllowWhenVisible:
      sanitized.osNotificationsAllowWhenVisible ?? defaultUiSettings.osNotificationsAllowWhenVisible,
    notifyOnNeedsInput: sanitized.notifyOnNeedsInput ?? defaultUiSettings.notifyOnNeedsInput,
    notifyOnIdle: sanitized.notifyOnIdle ?? defaultUiSettings.notifyOnIdle,
  }
}

function normalizeRecord(value: unknown): Record<string, string> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {}
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (typeof v === "string") out[k] = v
  }
  return out
}

function normalizeSpeechSettings(input?: Partial<SpeechSettings> | null): SpeechSettings {
  const sanitized = input ?? {}
  const sttModel =
    typeof sanitized.sttModel === "string" && sanitized.sttModel.trim()
      ? sanitized.sttModel.trim()
      : defaultSpeechSettings.sttModel
  const ttsModel =
    typeof sanitized.ttsModel === "string" && sanitized.ttsModel.trim()
      ? sanitized.ttsModel.trim()
      : defaultSpeechSettings.ttsModel
  return {
    provider: sanitized.provider === "openai-compatible" ? sanitized.provider : defaultSpeechSettings.provider,
    apiKey: typeof sanitized.apiKey === "string" && sanitized.apiKey.trim() ? sanitized.apiKey.trim() : undefined,
    hasApiKey: sanitized.hasApiKey === true || (typeof sanitized.apiKey === "string" && sanitized.apiKey.trim().length > 0),
    baseUrl: typeof sanitized.baseUrl === "string" && sanitized.baseUrl.trim() ? sanitized.baseUrl.trim() : undefined,
    sttModel,
    ttsModel,
    ttsVoice:
      typeof sanitized.ttsVoice === "string" && sanitized.ttsVoice.trim()
        ? sanitized.ttsVoice.trim()
        : defaultSpeechSettings.ttsVoice,
    playbackMode:
      sanitized.playbackMode === "buffered" || sanitized.playbackMode === "streaming"
        ? sanitized.playbackMode
        : defaultSpeechSettings.playbackMode,
    ttsFormat:
      sanitized.ttsFormat === "wav" || sanitized.ttsFormat === "opus" || sanitized.ttsFormat === "aac" || sanitized.ttsFormat === "mp3"
        ? sanitized.ttsFormat
        : defaultSpeechSettings.ttsFormat,
    separateProviders: sanitized.separateProviders === true,
    stt: {
      apiKey: typeof sanitized.stt?.apiKey === "string" && sanitized.stt.apiKey.trim() ? sanitized.stt.apiKey.trim() : undefined,
      hasApiKey: sanitized.stt?.hasApiKey === true || (typeof sanitized.stt?.apiKey === "string" && sanitized.stt.apiKey.trim().length > 0),
      baseUrl: typeof sanitized.stt?.baseUrl === "string" && sanitized.stt.baseUrl.trim() ? sanitized.stt.baseUrl.trim() : undefined,
      model:
        typeof sanitized.stt?.model === "string" && sanitized.stt.model.trim()
          ? sanitized.stt.model.trim()
          : sttModel,
    },
    tts: {
      apiKey: typeof sanitized.tts?.apiKey === "string" && sanitized.tts.apiKey.trim() ? sanitized.tts.apiKey.trim() : undefined,
      hasApiKey: sanitized.tts?.hasApiKey === true || (typeof sanitized.tts?.apiKey === "string" && sanitized.tts.apiKey.trim().length > 0),
      baseUrl: typeof sanitized.tts?.baseUrl === "string" && sanitized.tts.baseUrl.trim() ? sanitized.tts.baseUrl.trim() : undefined,
      model:
        typeof sanitized.tts?.model === "string" && sanitized.tts.model.trim()
          ? sanitized.tts.model.trim()
          : ttsModel,
    },
  }
}

function cloneArray<T>(value: unknown, mapper: (item: any) => T | null): T[] {
  if (!Array.isArray(value)) return []
  const out: T[] = []
  for (const item of value) {
    const mapped = mapper(item)
    if (mapped) out.push(mapped)
  }
  return out
}

function normalizeUiState(input?: UiStateBucket | null): NormalizedUiState {
  const source = input ?? {}
  return {
    recentFolders: cloneArray<RecentFolder>(source.recentFolders, (f) => {
      if (!f || typeof f !== "object") return null
      const p = (f as any).path
      const lastAccessed = (f as any).lastAccessed
      const projectName = (f as any).projectName
      if (typeof p !== "string") return null
      const ts = typeof lastAccessed === "number" ? lastAccessed : Date.now()
      return {
        path: p,
        lastAccessed: ts,
        ...(typeof projectName === "string" && projectName.trim() ? { projectName: projectName.trim() } : {}),
      }
    }),
    opencodeBinaries: cloneArray<OpenCodeBinary>(source.opencodeBinaries, (b) => {
      if (!b || typeof b !== "object") return null
      const p = (b as any).path
      if (typeof p !== "string") return null
      const lastUsed = typeof (b as any).lastUsed === "number" ? (b as any).lastUsed : Date.now()
      const version = typeof (b as any).version === "string" ? (b as any).version : undefined
      const label = typeof (b as any).label === "string" ? (b as any).label : undefined
      return { path: p, version, label, lastUsed }
    }),
    remoteServers: cloneArray<RemoteServerProfile>(source.remoteServers, (server) => {
      if (!server || typeof server !== "object") return null
      const id = typeof (server as any).id === "string" ? (server as any).id.trim() : ""
      const name = typeof (server as any).name === "string" ? (server as any).name.trim() : ""
      const baseUrl = typeof (server as any).baseUrl === "string" ? (server as any).baseUrl.trim() : ""
      if (!id || !name || !baseUrl) return null
      const createdAt = typeof (server as any).createdAt === "string" ? (server as any).createdAt : new Date().toISOString()
      const updatedAt = typeof (server as any).updatedAt === "string" ? (server as any).updatedAt : createdAt
      const lastConnectedAt = typeof (server as any).lastConnectedAt === "string" ? (server as any).lastConnectedAt : undefined
      return {
        id,
        name,
        baseUrl,
        skipTlsVerify: Boolean((server as any).skipTlsVerify),
        createdAt,
        updatedAt,
        lastConnectedAt,
      }
    }).sort((a, b) => {
      const left = a.lastConnectedAt ?? a.updatedAt
      const right = b.lastConnectedAt ?? b.updatedAt
      return right.localeCompare(left)
    }),
    models: {
      recents: cloneArray<ModelPreference>((source.models as any)?.recents, (m) => {
        if (!m || typeof m !== "object") return null
        const providerId = (m as any).providerId
        const modelId = (m as any).modelId
        if (typeof providerId !== "string" || typeof modelId !== "string") return null
        return { providerId, modelId }
      }),
      favorites: cloneArray<ModelPreference>((source.models as any)?.favorites, (m) => {
        if (!m || typeof m !== "object") return null
        const providerId = (m as any).providerId
        const modelId = (m as any).modelId
        if (typeof providerId !== "string" || typeof modelId !== "string") return null
        return { providerId, modelId }
      }),
      thinkingSelections: normalizeRecord((source.models as any)?.thinkingSelections),
      favoritesOnly: (source.models as any)?.favoritesOnly === true,
    },
  }
}

export function normalizeServerConfig(
  input?: ServerConfigBucket | null,
): Required<Pick<ServerConfigBucket, "listeningMode" | "logLevel" | "environmentVariables" | "opencodeBinary" | "secureEnvVars">> & { speech: SpeechSettings } {
  const source = input ?? {}
  const listeningMode = source.listeningMode === "all" ? "all" : "local"
  const logLevel =
    source.logLevel === "INFO" || source.logLevel === "WARN" || source.logLevel === "ERROR" || source.logLevel === "DEBUG"
      ? source.logLevel
      : "DEBUG"
  const opencodeBinary = typeof source.opencodeBinary === "string" && source.opencodeBinary.trim() && source.opencodeBinary !== "opencode"
    ? source.opencodeBinary
    : "opencode2"
  const environmentVariables = normalizeRecord(source.environmentVariables)
  const secureEnvVars = normalizeSecureEnvVars(source.secureEnvVars)
  const speech = normalizeSpeechSettings(source.speech)
  return { listeningMode, logLevel, opencodeBinary, environmentVariables, secureEnvVars, speech }
}

function normalizeSecureEnvVars(input?: unknown): string[] {
  if (!Array.isArray(input)) return []
  return input.filter((item): item is string => typeof item === "string" && item.length > 0)
}

function getModelKey(model: { providerId: string; modelId: string }): string {
  return `${model.providerId}/${model.modelId}`
}

function buildRecentFolderList(folderPath: string, source: RecentFolder[], aliasPath?: string, name?: string): RecentFolder[] {
  const matchingPaths = new Set([folderPath, aliasPath].filter((value): value is string => Boolean(value)))
  const aliasEntry = aliasPath ? source.find((folder) => folder.path === aliasPath) : undefined
  const canonicalEntry = source.find((folder) => folder.path === folderPath)
  const projectName = name?.trim() || aliasEntry?.projectName || canonicalEntry?.projectName
  const folders = source.filter((folder) => !matchingPaths.has(folder.path))
  folders.unshift({
    path: folderPath,
    lastAccessed: Date.now(),
    ...(projectName ? { projectName } : {}),
  })
  return folders.slice(0, MAX_RECENT_FOLDERS)
}

export function buildBinaryList(binaryPath: string, version: string | undefined, source: OpenCodeBinary[]): OpenCodeBinary[] {
  if (binaryPath === "opencode" || binaryPath === "opencode2") return source
  const timestamp = Date.now()
  const existing = source.find((b) => b.path === binaryPath)
  if (existing) {
    const updatedEntry: OpenCodeBinary = { ...existing, lastUsed: timestamp, version: version ?? existing.version }
    const remaining = source.filter((b) => b.path !== binaryPath)
    return [updatedEntry, ...remaining]
  }
  const nextEntry: OpenCodeBinary = version
    ? { path: binaryPath, version, lastUsed: timestamp }
    : { path: binaryPath, lastUsed: timestamp }
  return [nextEntry, ...source].slice(0, 10)
}

interface RemoteServerProfileInput {
  id?: string
  name: string
  baseUrl: string
  skipTlsVerify: boolean
}

function buildRemoteServerProfile(input: RemoteServerProfileInput, source: RemoteServerProfile[]): RemoteServerProfile {
  const existing = input.id ? source.find((entry) => entry.id === input.id) : undefined
  const now = new Date().toISOString()
  return {
    id: existing?.id ?? input.id ?? createRandomId(),
    name: input.name.trim(),
    baseUrl: input.baseUrl.trim(),
    skipTlsVerify: Boolean(input.skipTlsVerify),
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
    lastConnectedAt: existing?.lastConnectedAt,
  }
}

function buildRemoteServerList(profile: RemoteServerProfile, source: RemoteServerProfile[]): RemoteServerProfile[] {
  const remaining = source.filter((entry) => entry.id !== profile.id)
  return [profile, ...remaining].sort((a, b) => {
    const left = a.lastConnectedAt ?? a.updatedAt
    const right = b.lastConnectedAt ?? b.updatedAt
    return right.localeCompare(left)
  })
}

function createRandomId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID()
  }
  return `remote-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

const [uiConfigBucket, setUiConfigBucket] = createSignal<UiConfigBucket>({})
const [serverConfigBucket, setServerConfigBucket] = createSignal<ServerConfigBucket>({})
const [uiStateBucket, setUiStateBucket] = createSignal<UiStateBucket>({})
const [isLoaded, setIsLoaded] = createSignal(false)
// Mission inputs require a successful UI-owner read, not server/state readiness.
const [isUiConfigLoaded, setIsUiConfigLoaded] = createSignal(false)
const [uiConfigLoadFailed, setUiConfigLoadFailed] = createSignal(false)
const missionDefaultsValid = () => isUiConfigLoaded() && validMissionDefaults(missionPreferenceValue(uiConfigBucket(), "missionProfileDefaults"))
const missionModelsValid = () => isUiConfigLoaded() && validMissionModels(missionPreferenceValue(uiConfigBucket(), "missionModels"))
const missionPreferenceSnapshot = (key: MissionPreferenceKey) => missionPreferenceExpectation(uiConfigBucket(), key)

const uiSettings = createMemo<UiSettings>(() => normalizeUiSettings(uiConfigBucket().settings))
const legacyThemePreference = createMemo<ThemePreference>(() => uiStateBucket().theme ?? uiConfigBucket().theme ?? "system")
const colorSchemeOverrides = createMemo(() => normalizeColorSchemeOverrides(uiStateBucket().colorSchemeOverrides))
const colorSchemePreference = createMemo(() => {
  const scheme = normalizeColorScheme(uiStateBucket().colorScheme ?? uiConfigBucket().colorScheme, legacyThemePreference())
  if (scheme.id === "custom") {
    return scheme.appearance === "dark" && !uiStateBucket().activeColorSchemePresetId && isDefaultCustomColors(scheme.colors)
      ? normalizeColorScheme("basalt")
      : scheme
  }
  if (scheme.id === "system") return scheme
  const colors = colorSchemeOverrides()[scheme.id]
  return colors ? normalizeColorScheme({ ...scheme, colors }) : scheme
})
const customColorSchemePreference = createMemo(() => {
  const state = uiStateBucket()
  const config = uiConfigBucket()
  const custom = state.customColorScheme
    ?? config.customColorScheme
    ?? (colorSchemePreference().id === "custom" && !state.activeColorSchemePresetId ? state.colorScheme ?? config.colorScheme : undefined)
  const value = typeof custom === "object" && custom !== null && !Array.isArray(custom)
    ? { ...(custom as Record<string, unknown>), id: "custom" }
    : { id: "custom" }
  return normalizeColorScheme(value)
})
const colorSchemePresets = createMemo<UserColorSchemePresets>(() => normalizeColorSchemePresets(uiStateBucket().colorSchemePresets))
const activeColorSchemePresetId = createMemo(() => {
  const id = uiStateBucket().activeColorSchemePresetId
  return typeof id === "string" && colorSchemePresets()[id] ? id : undefined
})
const appearancePreferences = createMemo(() => normalizeAppearancePreferences(
  uiStateBucket().appearancePreferences, colorSchemePreference(), activeColorSchemePresetId(),
))
const themePreference = createMemo(() => appearancePreferences().mode)
function getAppearancePalette(appearance: Appearance): NormalizedColorScheme {
  const slot = appearancePreferences()[appearance]
  const preset = slot.presetId ? colorSchemePresets()[slot.presetId] : undefined
  if (preset?.appearance === appearance) return normalizeColorScheme({ id: "custom", ...preset })
  const scheme = slot.scheme
  const colors = scheme.id !== "custom" && scheme.id !== "system" ? colorSchemeOverrides()[scheme.id] : undefined
  return colors ? normalizeColorScheme({ ...scheme, colors }) : scheme
}
function getAppearancePresetId(appearance: Appearance): string | undefined {
  const id = appearancePreferences()[appearance].presetId
  return id && colorSchemePresets()[id]?.appearance === appearance ? id : undefined
}
const serverSettings = createMemo(() => normalizeServerConfig(serverConfigBucket()))
const uiState = createMemo(() => normalizeUiState(uiStateBucket()))

const preferences = uiSettings
const recentFolders = createMemo<RecentFolder[]>(() => uiState().recentFolders)
const opencodeBinaries = createMemo<OpenCodeBinary[]>(() => uiState().opencodeBinaries)
const remoteServers = createMemo<RemoteServerProfile[]>(() => uiState().remoteServers)

let loadPromise: Promise<void> | null = null

async function ensureLoaded(): Promise<void> {
  if (isLoaded()) return
  if (!loadPromise) {
    loadPromise = Promise.all([
      storage.revalidateUiConfigOwner().then(value => { setUiConfigBucket(value as any); setIsUiConfigLoaded(true); setUiConfigLoadFailed(false); return value })
        .catch(error => { setUiConfigLoadFailed(true); throw error }),
      storage.loadConfigOwner("server"),
      storage.loadStateOwner("ui"),
    ])
      .then(([, srvCfg, uiSt]) => {
        setServerConfigBucket(srvCfg as any)
        setUiStateBucket(uiSt as any)
        setIsLoaded(true)
      })
      .catch((error) => {
        log.error("Failed to load settings", error)
        setServerConfigBucket({})
        setUiStateBucket({})
        setIsLoaded(true)
      })
      .finally(() => {
        loadPromise = null
      })
  }
  await loadPromise
}

async function patchConfigOwner(owner: string, patch: unknown) {
  await ensureLoaded()
  let updated: OwnerBucket
  try { updated = await storage.patchConfigOwner(owner, patch) }
  catch (error) {
    if (owner === "ui" && error instanceof ConfigOwnerReconciliationPendingError) { setIsUiConfigLoaded(false); setUiConfigLoadFailed(true) }
    throw error
  }
  if (owner === "ui") setUiConfigBucket(updated as any)
  if (owner === "server") setServerConfigBucket(updated as any)
}

async function patchStateOwner(owner: string, patch: unknown) {
  await ensureLoaded()
  const updated = await storage.patchStateOwner(owner, patch)
  if (owner === "ui") setUiStateBucket(updated as any)
}

function updateUiSettings(updates: Partial<UiSettings>): Promise<boolean> {
  const current = uiConfigBucket()
  const nextSettings = normalizeUiSettings({ ...(current.settings ?? {}), ...updates })
  const patch = {
    settings: Object.fromEntries(
      Object.keys(updates).map((key) => [key, nextSettings[key as keyof UiSettings]]),
    ),
  }
  return patchConfigOwner("ui", patch).then(
    () => true,
    (error) => {
      log.error("Failed to patch ui settings", error)
      return false
    },
  )
}

function updatePreferences(updates: Partial<UiSettings>): Promise<boolean> {
  const keys = Object.keys(updates)
  const missionKeys = keys.filter((key): key is MissionPreferenceKey => key === "missionProfileDefaults" || key === "missionModels")
  if (missionKeys.length) {
    if (keys.length !== missionKeys.length || !isUiConfigLoaded()
      || (missionKeys.includes("missionModels") && (!missionModelsValid() || !validMissionModels(updates.missionModels)))
      || (missionKeys.includes("missionProfileDefaults") && (!missionDefaultsValid() || !validMissionDefaults(updates.missionProfileDefaults)))) return Promise.resolve(false)
    return patchMissionPreferences({ settings: updates }, missionKeys.map(missionPreferenceSnapshot))
      .then(() => true, () => false)
  }
  return updateUiSettings(updates)
}

// Preserve null deletion markers rather than passing resets through normalizers.
export function patchHotkeyPreferences(patch: { settings: { hotkeyOverrides: Record<string, HotkeyBinding[] | null> | null } }): Promise<boolean> {
  return patchConfigOwner("ui", patch).then(() => true, error => {
    log.error("Failed to patch hotkey settings", error)
    return false
  })
}

// Serialize local library edits and resolve the array only after preferences load.
// Targeted owner patches preserve all unrelated settings; no native config writes.
async function reloadMissionPreferences(): Promise<void> {
  try {
    const owner = await storage.revalidateUiConfigOwner()
    setUiConfigBucket(owner as any); setIsUiConfigLoaded(true); setUiConfigLoadFailed(false)
  } catch (error) { setIsUiConfigLoaded(false); setUiConfigLoadFailed(true); throw error }
}

async function patchMissionPreferences(patch: unknown, expected: MissionPreferenceExpectation[]): Promise<void> {
  setIsUiConfigLoaded(false)
  try {
    const owner = await storage.patchMissionPreferences(patch, expected)
    setUiConfigBucket(owner as any); setIsUiConfigLoaded(true); setUiConfigLoadFailed(false)
  } catch (error) {
    // A conflict, unknown mutation outcome or unordered acknowledged write is not
    // permission to freeze the old owner into another mission. Explicit reload
    // restores readiness; no mutation is retried here.
    setIsUiConfigLoaded(false); setUiConfigLoadFailed(true)
    throw error
  }
}

function saveMissionDefaults(input: MissionProfileDefault[], expected: MissionPreferenceExpectation, repair = false): Promise<void> {
  if (!isUiConfigLoaded() || (!missionDefaultsValid() && !repair) || !validMissionDefaults(input)
    || expected.key !== "missionProfileDefaults") return Promise.reject(new Error("Invalid mission defaults document"))
  return patchMissionPreferences({ settings: { missionProfileDefaults: encodeMissionDefaults(input) } }, [expected])
}

let missionModelWriteQueue = Promise.resolve()
function saveUserMissionModel(input: UserMissionModel): Promise<void> {
  const model = parseMissionModel(input)
  if (!missionModelsValid()) return Promise.reject(new Error("Invalid mission model document"))
  const expected = missionPreferenceSnapshot("missionModels")
  const current = normalizeMissionModels(expected.present ? expected.value : [])
  const write = missionModelWriteQueue.then(async () => {
    await patchMissionPreferences({ settings: { missionModels: saveMissionModelRecord(current, model) } }, [expected])
  })
  missionModelWriteQueue = write.catch(() => undefined)
  return write
}
function removeUserMissionModel(id: string, expected = missionPreferenceSnapshot("missionModels")): Promise<void> {
  if (!missionModelsValid()) return Promise.reject(new Error("Invalid mission model document"))
  if (expected.key !== "missionModels" || !validMissionModels(expected.present ? expected.value : undefined)) return Promise.reject(new Error("Invalid mission model expectation"))
  const current = normalizeMissionModels(expected.present ? expected.value : [])
  const write = missionModelWriteQueue.then(async () => {
    await patchMissionPreferences({ settings: { missionModels: removeMissionModelRecord(current, id) } }, [expected])
  })
  missionModelWriteQueue = write.catch(() => undefined)
  return write
}

function resetMissionModels(expected = missionPreferenceSnapshot("missionModels")): Promise<void> {
  if (!isUiConfigLoaded()) return Promise.reject(new Error("Mission preferences not loaded"))
  if (expected.key !== "missionModels") return Promise.reject(new Error("Invalid mission model expectation"))
  return patchMissionPreferences({ settings: { missionModels: [] } }, [expected])
}

const modelVisibilityWriteQueues = new Map<string, Promise<void>>()
let modelVisibilityWriteQueue = Promise.resolve()
const [pendingModelVisibility, setPendingModelVisibility] = createSignal(new Map<string, ModelVisibilityPreference>())
const [modelVisibilityWriteFailures, setModelVisibilityWriteFailures] = createSignal(new Set<string>())

function getProviderModelVisibilityPreference(providerId: string): ModelVisibilityPreference {
  return pendingModelVisibility().get(providerId)
    ?? normalizeModelVisibilityPreference(preferences().modelVisibility[providerId])
}

function providerModelVisibilitySaveFailed(providerId: string): boolean {
  return modelVisibilityWriteFailures().has(providerId)
}

async function setProviderModelVisibility(providerId: string, preference: ModelVisibilityPreference): Promise<void> {
  if (!providerId) return
  const normalized = normalizeModelVisibilityPreference(preference)
  setPendingModelVisibility((current) => new Map(current).set(providerId, normalized))
  setModelVisibilityWriteFailures((current) => {
    const next = new Set(current)
    next.delete(providerId)
    return next
  })
  const previous = modelVisibilityWriteQueue
  const write = previous
    .catch(() => undefined)
    .then(() => patchConfigOwner("ui", {
      settings: {
        modelVisibility: {
          [providerId]: normalized,
        },
      },
    }))

  modelVisibilityWriteQueue = write
  modelVisibilityWriteQueues.set(providerId, write)
  void write.then(
    () => {
      if (modelVisibilityWriteQueues.get(providerId) !== write) return
      modelVisibilityWriteQueues.delete(providerId)
      setPendingModelVisibility((current) => {
        const next = new Map(current)
        next.delete(providerId)
        return next
      })
    },
    (error) => {
      log.error("Failed to update provider model visibility", error)
      if (modelVisibilityWriteQueues.get(providerId) !== write) return
      modelVisibilityWriteQueues.delete(providerId)
      setPendingModelVisibility((current) => {
        const next = new Map(current)
        next.delete(providerId)
        return next
      })
      setModelVisibilityWriteFailures((current) => new Set(current).add(providerId))
    },
  )
  await write
}

function setThemePreference(preference: ThemePreference): Promise<void> {
  const write = colorSchemeWriteQueue.then(() => patchStateOwner("ui", {
    appearancePreferences: { ...appearancePreferences(), mode: preference }, theme: preference,
  }))
  colorSchemeWriteQueue = write.then(() => undefined, () => undefined)
  return write
}

let colorSchemeWriteQueue = Promise.resolve()

function paletteSelectionPatch(scheme: NormalizedColorScheme, presetId: string | null = null) {
  return {
    appearancePreferences: selectAppearancePalette(appearancePreferences(), scheme, presetId),
    theme: scheme.appearance === "system" ? "system" : themePreference(),
  }
}

function setColorSchemePreference(preference: NormalizedColorScheme): Promise<void> {
  const normalized = normalizeColorScheme(preference)
  const legacyTheme: ThemePreference = normalized.appearance === "system" ? "system" : normalized.appearance
  const patch = {
    theme: legacyTheme,
    colorScheme: toColorSchemeMergePatch(normalized),
    ...(normalized.id === "custom" ? { customColorScheme: normalized } : {}),
    activeColorSchemePresetId: null,
  }
  const write = colorSchemeWriteQueue.then(() => patchStateOwner("ui", { ...patch, ...paletteSelectionPatch(normalized) }))
  colorSchemeWriteQueue = write.then(() => undefined, () => undefined)
  return write.then(() => undefined).catch((error) => {
    log.error("Failed to set color scheme", error)
    throw error
  })
}

function selectColorSchemePreset(id: string): Promise<void> {
  const preset = colorSchemePresets()[id]
  if (!preset) return Promise.resolve()
  const scheme = normalizeColorScheme({ id: "custom", appearance: preset.appearance, colors: preset.colors })
  const write = colorSchemeWriteQueue.then(() => patchStateOwner("ui", {
    colorScheme: scheme,
    activeColorSchemePresetId: id,
    ...paletteSelectionPatch(scheme, id),
  }))
  colorSchemeWriteQueue = write.then(() => undefined, () => undefined)
  return write.then(() => undefined)
}

function saveColorSchemeOverride(id: Exclude<ColorSchemeId, "custom">, appearance: "light" | "dark", colors: Readonly<ColorSchemeColors>): Promise<void> {
  if (!isColorSchemeColors(colors)) return Promise.reject(new Error("Invalid color scheme override"))
  if (id === "system") return setColorSchemePreference(normalizeColorScheme({ id: "custom", appearance, colors }))
  const scheme = normalizeColorScheme({ id, appearance, colors })
  const write = colorSchemeWriteQueue.then(() => patchStateOwner("ui", {
    colorScheme: scheme,
    colorSchemeOverrides: { [id]: { ...colors } },
    activeColorSchemePresetId: null,
    ...paletteSelectionPatch(scheme),
  }))
  colorSchemeWriteQueue = write.then(() => undefined, () => undefined)
  return write.then(() => undefined)
}

function resetColorSchemeOverride(id: Exclude<ColorSchemeId, "custom">): Promise<void> {
  const scheme = normalizeColorScheme(id)
  const write = colorSchemeWriteQueue.then(() => patchStateOwner("ui", {
    colorScheme: toColorSchemeMergePatch(scheme),
    colorSchemeOverrides: { [id]: null },
    activeColorSchemePresetId: null,
    ...paletteSelectionPatch(scheme),
  }))
  colorSchemeWriteQueue = write.then(() => undefined, () => undefined)
  return write.then(() => undefined)
}

function saveColorSchemePreset(name: string, appearance: "light" | "dark", colors: Readonly<ColorSchemeColors>, presetId?: string): Promise<string> {
  const trimmedName = name.trim().slice(0, 80)
  if (!trimmedName || !isColorSchemeColors(colors)) return Promise.reject(new Error("Invalid color scheme preset"))
  if (!presetId && Object.keys(colorSchemePresets()).length >= MAX_COLOR_SCHEME_PRESETS) return Promise.reject(new Error("Color scheme preset limit reached"))
  if (presetId && !colorSchemePresets()[presetId]) return Promise.reject(new Error("Unknown color scheme preset"))
  const id = presetId ?? createColorSchemePresetId()
  const scheme = normalizeColorScheme({ id: "custom", appearance, colors })
  const write = colorSchemeWriteQueue.then(() => patchStateOwner("ui", {
    colorScheme: scheme,
    colorSchemePresets: { [id]: { name: trimmedName, appearance, colors: { ...colors } } },
    activeColorSchemePresetId: id,
    ...paletteSelectionPatch(scheme, id),
  }))
  colorSchemeWriteQueue = write.then(() => undefined, () => undefined)
  return write.then(() => id)
}

function deleteColorSchemePreset(id: string): Promise<void> {
  if (!colorSchemePresets()[id]) return Promise.resolve()
  const write = colorSchemeWriteQueue.then(() => patchStateOwner("ui", {
    colorSchemePresets: { [id]: null },
    appearancePreferences: Object.fromEntries((["light", "dark"] as const)
      .filter((appearance) => appearancePreferences()[appearance].presetId === id)
      .map((appearance) => [appearance, { scheme: getAppearancePalette(appearance), presetId: null }])),
    ...(activeColorSchemePresetId() === id ? { activeColorSchemePresetId: null } : {}),
  }))
  colorSchemeWriteQueue = write.then(() => undefined, () => undefined)
  return write.then(() => undefined)
}

 async function setListeningMode(mode: ListeningMode): Promise<void> {
   if (serverSettings().listeningMode === mode) return
   await patchConfigOwner("server", { listeningMode: mode })
 }

/** Saves which releases are offered; desktop data and the backend stay unchanged. */
async function setUpdateFeed(feed: UpdateFeed): Promise<void> {
  await patchConfigOwner("server", { updateFeed: feed })
}

function updateEnvironmentVariables(envVars: Record<string, string>): void {
  void patchConfigOwner("server", { environmentVariables: envVars }).catch((error) =>
    log.error("Failed to update environment variables", error),
  )
}

function addEnvironmentVariable(key: string, value: string, secure: boolean = true): void {
  const current = serverSettings().environmentVariables
  updateEnvironmentVariables({ ...current, [key]: value })

  const secureList = serverSettings().secureEnvVars
  const upperKey = key.toUpperCase()
  const exists = secureList.some((name) => name.toUpperCase() === upperKey)

  if (secure) {
    if (!exists) {
      const next = [...secureList, key]
      void patchConfigOwner("server", { secureEnvVars: next }).catch((error) =>
        log.error("Failed to add secure env var", error),
      )
    }
  } else {
    if (exists) {
      const next = secureList.filter((name) => name.toUpperCase() !== upperKey)
      void patchConfigOwner("server", { secureEnvVars: next }).catch((error) =>
        log.error("Failed to remove secure env var", error),
      )
    }
  }
}

function removeEnvironmentVariable(key: string): void {
  void patchConfigOwner("server", { environmentVariables: { [key]: null } }).catch((error) =>
    log.error("Failed to remove environment variable", error),
  )
}

function isSecureEnvVar(key: string): boolean {
  const secureList = serverSettings().secureEnvVars
  return secureList.some((name) => name.toUpperCase() === key.toUpperCase())
}

function toggleSecureEnvVar(key: string): void {
  const secureList = serverSettings().secureEnvVars
  const upperKey = key.toUpperCase()
  const exists = secureList.some((name) => name.toUpperCase() === upperKey)
  const next = exists
    ? secureList.filter((name) => name.toUpperCase() !== upperKey)
    : [...secureList, key]
  void patchConfigOwner("server", { secureEnvVars: next }).catch((error) =>
    log.error("Failed to update secure env vars", error),
  )
}

function updateLastUsedBinary(path: string): void {
  const target = path && path.trim().length > 0 ? path : "opencode2"
  void patchConfigOwner("server", { opencodeBinary: target }).catch((error) => log.error("Failed to set default binary", error))

  // also bump lastUsed in state ui.opencodeBinaries
  const nextList = buildBinaryList(target, undefined, opencodeBinaries())
  void patchStateOwner("ui", { opencodeBinaries: nextList }).catch((error) => log.error("Failed to update binary list", error))
}

function updateLogLevel(level: ServerLogLevel): void {
  const target = level ?? "DEBUG"
  void patchConfigOwner("server", { logLevel: target }).catch((error) => log.error("Failed to set log level", error))
}

async function updateSpeechSettings(updates: SpeechSettingsUpdate): Promise<void> {
  const patch = buildSpeechPatch(updates)
  try {
    await patchConfigOwner("server", { speech: patch })
  } catch (error) {
    log.error("Failed to update speech settings", error)
    throw error
  }
}

function addOpenCodeBinary(path: string, version?: string): void {
  const nextList = buildBinaryList(path, version, opencodeBinaries())
  void patchStateOwner("ui", { opencodeBinaries: nextList }).catch((error) => log.error("Failed to add binary", error))
}

function removeOpenCodeBinary(path: string): void {
  const nextList = opencodeBinaries().filter((b) => b.path !== path)
  void patchStateOwner("ui", { opencodeBinaries: nextList }).catch((error) => log.error("Failed to remove binary", error))

  if (serverSettings().opencodeBinary === path) {
    void patchConfigOwner("server", { opencodeBinary: "opencode2" }).catch((error) =>
      log.error("Failed to reset default binary", error),
    )
  }
}

function addRecentFolder(folderPath: string): void {
  const next = buildRecentFolderList(folderPath, recentFolders())
  void patchStateOwner("ui", { recentFolders: next }).catch((error) => log.error("Failed to add recent folder", error))
}

function removeRecentFolder(folderPath: string): void {
  const next = recentFolders().filter((f) => f.path !== folderPath)
  void patchStateOwner("ui", { recentFolders: next }).catch((error) => log.error("Failed to remove recent folder", error))
}

async function renameRecentFolderProject(folderPath: string, projectName: string): Promise<void> {
  const name = projectName.trim()
  if (!folderPath || !name) return
  // Derive from loaded state; an unloaded empty list would erase every recent folder.
  await ensureLoaded()
  const next = recentFolders().map((folder) => (folder.path === folderPath ? { ...folder, projectName: name } : folder))
  try {
    await patchStateOwner("ui", { recentFolders: next })
  } catch (error) {
    log.error("Failed to rename recent folder", error)
    throw error
  }
}

async function saveRemoteServerProfile(input: RemoteServerProfileInput): Promise<RemoteServerProfile> {
  const profile = buildRemoteServerProfile(input, remoteServers())
  await patchStateOwner("ui", { remoteServers: buildRemoteServerList(profile, remoteServers()) })
  return profile
}

async function markRemoteServerConnected(id: string): Promise<void> {
  const current = remoteServers().find((entry) => entry.id === id)
  if (!current) return
  const now = new Date().toISOString()
  const updated: RemoteServerProfile = {
    ...current,
    updatedAt: now,
    lastConnectedAt: now,
  }
  await patchStateOwner("ui", { remoteServers: buildRemoteServerList(updated, remoteServers()) })
}

function removeRemoteServerProfile(id: string): void {
  const next = remoteServers().filter((entry) => entry.id !== id)
  void patchStateOwner("ui", { remoteServers: next }).catch((error) => log.error("Failed to remove remote server", error))
}

function recordWorkspaceLaunch(folderPath: string, aliasPath?: string): void {
  const nextFolders = buildRecentFolderList(folderPath, recentFolders(), aliasPath)

  void patchStateOwner("ui", { recentFolders: nextFolders }).catch((error) =>
    log.error("Failed to update ui state on launch", error),
  )
}

/** Adds a named project to the recent folders, e.g. a kept temporary folder. */
async function addNamedRecentFolder(folderPath: string, projectName: string): Promise<void> {
  await ensureLoaded()
  await patchStateOwner("ui", { recentFolders: buildRecentFolderList(folderPath, recentFolders(), undefined, projectName) })
}

function addRecentModelPreference(model: ModelPreference): void {
  if (!model.providerId || !model.modelId) return
  const recents = uiState().models.recents
  const filtered = recents.filter((item) => item.providerId !== model.providerId || item.modelId !== model.modelId)
  const updated = [model, ...filtered].slice(0, MAX_RECENT_MODELS)
  void patchStateOwner("ui", { models: { recents: updated } }).catch((error) => log.error("Failed to update model recents", error))
}

function isFavoriteModelPreference(model: ModelPreference): boolean {
  if (!model.providerId || !model.modelId) return false
  return uiState().models.favorites.some((item) => item.providerId === model.providerId && item.modelId === model.modelId)
}

function toggleFavoriteModelPreference(model: ModelPreference): void {
  if (!model.providerId || !model.modelId) return
  const favorites = uiState().models.favorites
  const exists = favorites.some((item) => item.providerId === model.providerId && item.modelId === model.modelId)

  const updated = exists
    ? favorites.filter((item) => item.providerId !== model.providerId || item.modelId !== model.modelId)
    : [model, ...favorites.filter((item) => item.providerId !== model.providerId || item.modelId !== model.modelId)].slice(
        0,
        MAX_FAVORITE_MODELS,
      )

  void patchStateOwner("ui", { models: { favorites: updated } }).catch((error) => log.error("Failed to update model favorites", error))
}

// The mode is read back by the picker, so the wanted value is published
// synchronously and the persisted writes are serialized in click order.
const [pendingFavoritesOnly, setPendingFavoritesOnly] = createSignal<boolean | undefined>(undefined)
let favoritesOnlyWriteQueue: Promise<void> = Promise.resolve()
let latestFavoritesOnlyWrite: Promise<void> | null = null

function getFavoritesOnlyPreference(): boolean {
  return pendingFavoritesOnly() ?? uiState().models.favoritesOnly
}

function setFavoritesOnlyPreference(enabled: boolean): void {
  if (getFavoritesOnlyPreference() === enabled) return
  setPendingFavoritesOnly(enabled)

  const write = favoritesOnlyWriteQueue
    .catch(() => undefined)
    .then(async () => {
      try {
        await patchStateOwner("ui", { models: { favoritesOnly: enabled } })
      } catch (error) {
        log.error("Failed to update favorites-only model mode", error)
      }
    })

  favoritesOnlyWriteQueue = write
  latestFavoritesOnlyWrite = write
  void write.then(() => {
    // Only the newest write may retire the published intent, so a superseded
    // write inside a click burst cannot fall back to an older stored value.
    if (latestFavoritesOnlyWrite !== write) return
    latestFavoritesOnlyWrite = null
    setPendingFavoritesOnly((current) => (current === enabled ? undefined : current))
  })
}

function getModelThinkingSelection(model: { providerId: string; modelId: string }): string | undefined {
  if (!model.providerId || !model.modelId) return undefined
  return uiState().models.thinkingSelections[getModelKey(model)]
}

function setModelThinkingSelection(model: { providerId: string; modelId: string }, value: string | undefined): void {
  if (!model.providerId || !model.modelId) return
  const key = getModelKey(model)
  const current = uiState().models.thinkingSelections[key]
  if (current === value) return

  const selections = { ...uiState().models.thinkingSelections }
  if (!value) {
    delete selections[key]
  } else {
    selections[key] = value
  }
  void patchStateOwner("ui", { models: { thinkingSelections: selections } }).catch((error) =>
    log.error("Failed to update thinking selection", error),
  )
}

function setDiffViewMode(mode: DiffViewMode): void {
  if (preferences().diffViewMode === mode) return
  updateUiSettings({ diffViewMode: mode })
}

function setToolOutputExpansion(mode: VisibilityPreference): void {
  const current = preferences()
  if (current.toolCallExpansionDefaults.tools.other === mode) return
  updateUiSettings({
    toolOutputExpansion: mode === "hidden" ? current.toolOutputExpansion : mode,
    toolCallExpansionDefaults: {
      ...current.toolCallExpansionDefaults,
      preset: "custom",
      tools: {
        ...current.toolCallExpansionDefaults.tools,
        other: mode,
      },
    },
  })
}

function setDiagnosticsExpansion(mode: VisibilityPreference): void {
  if (preferences().diagnosticsExpansion === mode) return
  updateUiSettings({ diagnosticsExpansion: mode })
}

function setToolInputsVisibility(mode: ToolInputsVisibilityPreference): void {
  if (preferences().toolInputsVisibility === mode) return
  updateUiSettings({ toolInputsVisibility: mode })
}

function setThinkingBlocksExpansion(mode: ExpansionPreference): void {
  const current = preferences()
  if (current.thinkingBlocksExpansion === mode && current.toolCallExpansionDefaults.thinking === mode) return
  updateUiSettings({
    thinkingBlocksExpansion: mode,
    toolCallExpansionDefaults: {
      ...current.toolCallExpansionDefaults,
      preset: "custom",
      thinking: mode,
    },
  })
}

function toggleShowThinkingBlocks(): void {
  updateUiSettings({ showThinkingBlocks: !preferences().showThinkingBlocks })
}

function toggleShowMessageTimeline(): void {
  updateUiSettings({ showMessageTimeline: !(preferences().showMessageTimeline ?? true) })
}

function toggleUsageMetrics(): void {
  updateUiSettings({ showUsageMetrics: !preferences().showUsageMetrics })
}

function toggleShowPromptVoiceInput(): void {
  updateUiSettings({ showPromptVoiceInput: !preferences().showPromptVoiceInput })
}

function toggleAutoCleanupBlankSessions(): void {
  const nextValue = !preferences().autoCleanupBlankSessions
  log.info("toggle auto cleanup", { value: nextValue })
  updateUiSettings({ autoCleanupBlankSessions: nextValue })
}

async function setAgentModelPreference(instanceId: string, agent: string, model: ModelPreference): Promise<void> {
  if (!instanceId || !agent || !model.providerId || !model.modelId) return
  await ensureInstanceConfigLoaded(instanceId)
  await updateInstanceData(instanceId, (draft) => {
    const selections = { ...(draft.agentModelSelections ?? {}) }
    const existing = selections[agent]
    if (existing && existing.providerId === model.providerId && existing.modelId === model.modelId) {
      return
    }
    selections[agent] = model
    draft.agentModelSelections = selections
  })
}

async function getAgentModelPreference(instanceId: string, agent: string): Promise<ModelPreference | undefined> {
  if (!instanceId || !agent) return undefined
  await ensureInstanceConfigLoaded(instanceId)
  const selections = getInstanceConfig(instanceId).agentModelSelections ?? {}
  return selections[agent]
}

void ensureLoaded().catch((error: unknown) => {
  log.error("Failed to initialize settings", error)
})

interface ConfigContextValue {
  isLoaded: Accessor<boolean>
  isUiConfigLoaded: typeof isUiConfigLoaded
  uiConfigLoadFailed: typeof uiConfigLoadFailed
  missionDefaultsValid: typeof missionDefaultsValid
  missionModelsValid: typeof missionModelsValid
  missionPreferenceSnapshot: typeof missionPreferenceSnapshot
  reloadMissionPreferences: typeof reloadMissionPreferences
  saveMissionDefaults: typeof saveMissionDefaults
  resetMissionModels: typeof resetMissionModels
  preferences: typeof preferences
  updatePreferences: typeof updatePreferences
  saveUserMissionModel: typeof saveUserMissionModel
  removeUserMissionModel: typeof removeUserMissionModel
  setProviderModelVisibility: typeof setProviderModelVisibility
  getProviderModelVisibilityPreference: typeof getProviderModelVisibilityPreference
  providerModelVisibilitySaveFailed: typeof providerModelVisibilitySaveFailed
  themePreference: typeof themePreference
  setThemePreference: typeof setThemePreference
  getAppearancePalette: typeof getAppearancePalette
  getAppearancePresetId: typeof getAppearancePresetId
  colorSchemePreference: typeof colorSchemePreference
  customColorSchemePreference: typeof customColorSchemePreference
  colorSchemeOverrides: typeof colorSchemeOverrides
  colorSchemePresets: typeof colorSchemePresets
  activeColorSchemePresetId: typeof activeColorSchemePresetId
  setColorSchemePreference: typeof setColorSchemePreference
  selectColorSchemePreset: typeof selectColorSchemePreset
  saveColorSchemeOverride: typeof saveColorSchemeOverride
  resetColorSchemeOverride: typeof resetColorSchemeOverride
  saveColorSchemePreset: typeof saveColorSchemePreset
  deleteColorSchemePreset: typeof deleteColorSchemePreset

  // server-owned stable config
  serverSettings: typeof serverSettings
  setListeningMode: typeof setListeningMode
  updateEnvironmentVariables: typeof updateEnvironmentVariables
  addEnvironmentVariable: typeof addEnvironmentVariable
  removeEnvironmentVariable: typeof removeEnvironmentVariable
  isSecureEnvVar: typeof isSecureEnvVar
  toggleSecureEnvVar: typeof toggleSecureEnvVar
    updateLastUsedBinary: typeof updateLastUsedBinary
    updateLogLevel: typeof updateLogLevel
    updateSpeechSettings: typeof updateSpeechSettings

  // ui-owned state
  recentFolders: typeof recentFolders
  opencodeBinaries: typeof opencodeBinaries
  remoteServers: typeof remoteServers
  uiState: typeof uiState
  addRecentFolder: typeof addRecentFolder
  removeRecentFolder: typeof removeRecentFolder
  renameRecentFolderProject: typeof renameRecentFolderProject
  addOpenCodeBinary: typeof addOpenCodeBinary
  removeOpenCodeBinary: typeof removeOpenCodeBinary
  saveRemoteServerProfile: typeof saveRemoteServerProfile
  markRemoteServerConnected: typeof markRemoteServerConnected
  removeRemoteServerProfile: typeof removeRemoteServerProfile
  recordWorkspaceLaunch: typeof recordWorkspaceLaunch
  addRecentModelPreference: typeof addRecentModelPreference
  isFavoriteModelPreference: typeof isFavoriteModelPreference
  toggleFavoriteModelPreference: typeof toggleFavoriteModelPreference
  getFavoritesOnlyPreference: typeof getFavoritesOnlyPreference
  setFavoritesOnlyPreference: typeof setFavoritesOnlyPreference
  getModelThinkingSelection: typeof getModelThinkingSelection
  setModelThinkingSelection: typeof setModelThinkingSelection

  // ui settings helpers
  toggleShowThinkingBlocks: typeof toggleShowThinkingBlocks
  toggleShowMessageTimeline: typeof toggleShowMessageTimeline
  toggleUsageMetrics: typeof toggleUsageMetrics
  toggleAutoCleanupBlankSessions: typeof toggleAutoCleanupBlankSessions
  toggleShowPromptVoiceInput: typeof toggleShowPromptVoiceInput
  setDiffViewMode: typeof setDiffViewMode
  setToolOutputExpansion: typeof setToolOutputExpansion
  setDiagnosticsExpansion: typeof setDiagnosticsExpansion
  setThinkingBlocksExpansion: typeof setThinkingBlocksExpansion
  setToolInputsVisibility: typeof setToolInputsVisibility

  // instance scoped
  setAgentModelPreference: typeof setAgentModelPreference
  getAgentModelPreference: typeof getAgentModelPreference
}

const ConfigContext = createContext<ConfigContextValue>()

const configContextValue: ConfigContextValue = {
  isLoaded,
  isUiConfigLoaded,
  uiConfigLoadFailed,
  missionDefaultsValid,
  missionModelsValid,
  missionPreferenceSnapshot,
  reloadMissionPreferences,
  saveMissionDefaults,
  resetMissionModels,
  preferences,
  updatePreferences,
  saveUserMissionModel,
  removeUserMissionModel,
  setProviderModelVisibility,
  getProviderModelVisibilityPreference,
  providerModelVisibilitySaveFailed,
  themePreference,
  setThemePreference,
  colorSchemePreference,
  getAppearancePalette,
  getAppearancePresetId,
  customColorSchemePreference,
  colorSchemeOverrides,
  colorSchemePresets,
  activeColorSchemePresetId,
  setColorSchemePreference,
  selectColorSchemePreset,
  saveColorSchemeOverride,
  resetColorSchemeOverride,
  saveColorSchemePreset,
  deleteColorSchemePreset,
  serverSettings,
  setListeningMode,
  updateEnvironmentVariables,
  addEnvironmentVariable,
  removeEnvironmentVariable,
  isSecureEnvVar,
  toggleSecureEnvVar,
  updateLastUsedBinary,
  updateLogLevel,
  updateSpeechSettings,
  recentFolders,
  opencodeBinaries,
  remoteServers,
  uiState,
  addRecentFolder,
  removeRecentFolder,
  renameRecentFolderProject,
  addOpenCodeBinary,
  removeOpenCodeBinary,
  saveRemoteServerProfile,
  markRemoteServerConnected,
  removeRemoteServerProfile,
  recordWorkspaceLaunch,
  addRecentModelPreference,
  isFavoriteModelPreference,
  toggleFavoriteModelPreference,
  getFavoritesOnlyPreference,
  setFavoritesOnlyPreference,
  getModelThinkingSelection,
  setModelThinkingSelection,
  toggleShowThinkingBlocks,
  toggleShowMessageTimeline,
  toggleUsageMetrics,
  toggleAutoCleanupBlankSessions,
  toggleShowPromptVoiceInput,
  setDiffViewMode,
  setToolOutputExpansion,
  setDiagnosticsExpansion,
  setThinkingBlocksExpansion,
  setToolInputsVisibility,
  setAgentModelPreference,
  getAgentModelPreference,
}

export const ConfigProvider: ParentComponent = (props) => {
  onMount(() => {
    ensureLoaded().catch((error: unknown) => {
      log.error("Failed to initialize settings", error)
    })

    const unsubUi = storage.onConfigOwnerChanged("ui", (bucket) => {
      setUiConfigBucket(bucket as any)
    })
    const unsubServer = storage.onConfigOwnerChanged("server", (bucket) => {
      setServerConfigBucket(bucket as any)
      resetSpeechCapabilities()
      void loadSpeechCapabilities(true)
    })
    const unsubStateUi = storage.onStateOwnerChanged("ui", (bucket) => {
      setUiStateBucket(bucket as any)
    })

    return () => {
      unsubUi()
      unsubServer()
      unsubStateUi()
    }
  })

  return <ConfigContext.Provider value={configContextValue}>{props.children}</ConfigContext.Provider>
}

export function useConfig(): ConfigContextValue {
  const context = useContext(ConfigContext)
  if (!context) {
    throw new Error("useConfig must be used within ConfigProvider")
  }
  return context
}

export {
  preferences,
  uiState,
  serverSettings,
  recentFolders,
  opencodeBinaries,
  themePreference,
  setThemePreference,
  colorSchemePreference,
  getAppearancePalette,
  getAppearancePresetId,
  customColorSchemePreference,
  colorSchemeOverrides,
  colorSchemePresets,
  activeColorSchemePresetId,
  setColorSchemePreference,
  selectColorSchemePreset,
  saveColorSchemeOverride,
  resetColorSchemeOverride,
  saveColorSchemePreset,
  deleteColorSchemePreset,
  updatePreferences,
  setProviderModelVisibility,
  getProviderModelVisibilityPreference,
  providerModelVisibilitySaveFailed,
  setListeningMode,
  setUpdateFeed,
  updateEnvironmentVariables,
  addEnvironmentVariable,
  removeEnvironmentVariable,
  isSecureEnvVar,
  toggleSecureEnvVar,
  updateLastUsedBinary,
  updateLogLevel,
  updateSpeechSettings,
  addRecentFolder,
  removeRecentFolder,
  renameRecentFolderProject,
  addNamedRecentFolder,
  addOpenCodeBinary,
  removeOpenCodeBinary,
  recordWorkspaceLaunch,
  addRecentModelPreference,
  isFavoriteModelPreference,
  toggleFavoriteModelPreference,
  getFavoritesOnlyPreference,
  setFavoritesOnlyPreference,
  getModelThinkingSelection,
  setModelThinkingSelection,
  toggleShowThinkingBlocks,
  toggleUsageMetrics,
  toggleAutoCleanupBlankSessions,
  toggleShowPromptVoiceInput,
  setDiffViewMode,
  setToolOutputExpansion,
  setDiagnosticsExpansion,
  setThinkingBlocksExpansion,
  setAgentModelPreference,
  getAgentModelPreference,
}

